import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Envelope, FlowArrow } from '@phosphor-icons/react'
import { cn } from '@/lib/utils'
import { handleTabListKeyDown } from '@/lib/rovingTabs'
import EmailTemplateList from './EmailTemplateList'
import WorkflowTemplateGallery from './WorkflowTemplateGallery'

type TemplateTab = 'email' | 'workflow'

const TemplatesPage: React.FC = () => {
  const location = useLocation()
  // Land on the workflow tab when returning from a template-launched builder.
  const initialTab: TemplateTab =
    (location.state as { tab?: TemplateTab } | null)?.tab === 'workflow' ? 'workflow' : 'email'
  const [activeTab, setActiveTab] = useState<TemplateTab>(initialTab)

  return (
    <div className="space-y-6">
      {/* Header + Tab switcher */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 pb-3 border-b border-border/40 dark:border-white/[0.05]">
        <div className="min-w-0">
          <h1
            className="text-foreground dark:text-[#f7f8f8] truncate"
            style={{
              fontSize: '24px',
              fontWeight: 590,
              lineHeight: 1.33,
              letterSpacing: '-0.288px',
            }}
          >
            Templates
          </h1>
          <p
            className="text-muted-foreground dark:text-[#8a8f98] mt-1"
            style={{ fontSize: '13px', lineHeight: 1.5, letterSpacing: '-0.13px' }}
          >
            {activeTab === 'email'
              ? 'Manage email structure templates that define how your emails are built'
              : 'Browse prebuilt workflow templates to jumpstart your marketing automation'
            }
          </p>
        </div>

        <div
          className="inline-flex p-1 gap-1 rounded-md bg-muted/50 border border-border/50 dark:bg-white/[0.03] dark:border-white/[0.06] flex-shrink-0"
          role="tablist"
          aria-label="Template category"
          onKeyDown={(e) =>
            handleTabListKeyDown(e, {
              activeIndex: activeTab === 'email' ? 0 : 1,
              count: 2,
              onSelect: (i) => setActiveTab(i === 0 ? 'email' : 'workflow'),
            })
          }
        >
          <button
            type="button"
            role="tab"
            id="templates-tab-email"
            aria-controls="templates-panel-email"
            aria-selected={activeTab === 'email'}
            tabIndex={activeTab === 'email' ? 0 : -1}
            className={cn(
              'inline-flex items-center gap-1.5 px-3.5 py-1 rounded border transition-colors duration-150',
              activeTab === 'email'
                ? 'bg-background text-foreground border-border/60 shadow-sm dark:bg-white/[0.06] dark:text-[#f7f8f8] dark:border-white/[0.10] dark:shadow-none'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-background/60 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.03]',
            )}
            style={{
              fontSize: '13px',
              fontWeight: activeTab === 'email' ? 590 : 510,
              letterSpacing: '-0.13px',
            }}
            onClick={() => setActiveTab('email')}
          >
            <Envelope className="h-4 w-4" weight="regular" />
            Email templates
          </button>
          <button
            type="button"
            role="tab"
            id="templates-tab-workflow"
            aria-controls="templates-panel-workflow"
            aria-selected={activeTab === 'workflow'}
            tabIndex={activeTab === 'workflow' ? 0 : -1}
            className={cn(
              'inline-flex items-center gap-1.5 px-3.5 py-1 rounded border transition-colors duration-150',
              activeTab === 'workflow'
                ? 'bg-background text-foreground border-border/60 shadow-sm dark:bg-white/[0.06] dark:text-[#f7f8f8] dark:border-white/[0.10] dark:shadow-none'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-background/60 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.03]',
            )}
            style={{
              fontSize: '13px',
              fontWeight: activeTab === 'workflow' ? 590 : 510,
              letterSpacing: '-0.13px',
            }}
            onClick={() => setActiveTab('workflow')}
          >
            <FlowArrow className="h-4 w-4" weight="regular" />
            Workflow templates
          </button>
        </div>
      </div>

      {/* Tab content */}
      <div
        role="tabpanel"
        id={activeTab === 'email' ? 'templates-panel-email' : 'templates-panel-workflow'}
        aria-labelledby={activeTab === 'email' ? 'templates-tab-email' : 'templates-tab-workflow'}
      >
        {activeTab === 'email' ? <EmailTemplateList /> : <WorkflowTemplateGallery />}
      </div>
    </div>
  )
}

export default TemplatesPage
